import { Component, Output, EventEmitter, ViewChild, OnInit, DoCheck, AfterViewInit, OnDestroy, HostListener, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDrawer, MatDialog } from '@angular/material';
import { User } from 'src/app/models/user';
import { LoginService } from 'src/app/managers/login.service';
import { FunctionsService } from 'src/app/common/services/functions.service';
import { CoreResponseMessage, CoreResponseStatus } from 'src/app/contracts/responseMessage/coreResponseMessage';
import { BaseComponent } from 'src/app/common/components/base/base.component';
import { ClientConfigService } from 'src/app/common/services/client-config.service';
import { OutputDataService } from 'src/app/common/services/output-data.service';
import { Subscription } from 'rxjs';
import { ForgotPasswordComponent } from 'src/app/components/forgot-password/forgot-password.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent extends BaseComponent implements OnInit, DoCheck, AfterViewInit, OnDestroy {
  @Output() onLogin: EventEmitter<User> = new EventEmitter<User>();
  @ViewChild('drawer', { static: true }) drawer: MatDrawer;
  @ViewChild('userNameField', { static: true }) userNameElement: ElementRef;
  private _onMenuOpenedSubscription: Subscription;
  email: string;
  password: string;
  confirmPassword: string;
  submittable: boolean;
  componentHeight: string;
  isLogin: boolean;
  cardTitle: string;

  constructor(route: ActivatedRoute, loginManager: LoginService, clientConfig: ClientConfigService, dialog: MatDialog, private _functionsService: FunctionsService,
              private _snackBar: MatSnackBar, private _router: Router, private _outputDataService: OutputDataService) {
    super(route, loginManager, clientConfig, dialog);
    this.setComponentHeight();
  }

  @HostListener('window:resize', ['$event'])
  getScreenSize(event?) {
    this.setComponentHeight();
  }

  ngOnInit() {
    super.ngOnInit();
    this.dataSource = this.getLoggedinUser();      
    this.email = this.dataSource.EmailAddress;    
    this.password = '';
    this.submittable = this.isSubmittable();
    this._onMenuOpenedSubscription = this._outputDataService.onMenuOpened.subscribe(x => this.toggleMenu(x));
    
    if (this.params['sideMenu'] === 'signin') {
      this.cardTitle = "Log in";
      this.isLogin = true;
    }
    else {
      this.cardTitle = "Sign up";
      this.isLogin = false;
    }
  }

  ngDoCheck() {    
    this.submittable = this.isSubmittable();
    
    if (this.params['sideMenu'] === 'signin') {
      this.cardTitle = "Log in";
      this.isLogin = true;
    }
    else {
      this.cardTitle = "Sign up";
      this.isLogin = false;
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.userNameElement.nativeElement.focus();
    }, 0);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._onMenuOpenedSubscription.unsubscribe();
  }

  submitAction() {
    if (this.isLogin)
      this.submitLoginAsync();
    else
      this.submitSignupAsync();
  }

  async submitLoginAsync() {
    this._snackBar.open('Logging in...');
    let coreResponseMsg = await this.loginManager.loginAsync(this.email, this.password);
    this._snackBar.dismiss();

    if (coreResponseMsg.Status === CoreResponseStatus.OK) {
      this.dataSource = this.getLoggedinUser();
      let name = this.dataSource.Email.split('@', 2);
      this.onLogin.emit(this.dataSource);
      this._snackBar.open('Welcome ' + name[0] + '!', 'OK', { duration: this._successMsgDuration });
      
      /*
      we want to stay in the root url to prevent the error of 404 file or directory not found when users refresh page from a browser. 
      this._router.navigate(['/home']);
      */
      this._router.navigate(['']);
    }      
    else {
      let alertStr = coreResponseMsg.Message + ' Please try again with correct username and password.';
      this.openAlertDialog('Log in', alertStr, '400px', '235px');
      this.handleCoreResponseMessageError(coreResponseMsg);
    }
  }

  async submitSignupAsync() {
    if (this.password !== this.confirmPassword) {
      this.openAlertDialog('Sign up', "Passwords don't match", '350px', '200px');
      return;
    }

    this._snackBar.open('Signing up and configuring for the first time...');
    let coreResponseMsg = await this.loginManager.signupAsync(this.email, this.password);
    this._snackBar.dismiss();

    if (coreResponseMsg.Status === CoreResponseStatus.OK) {
      this.dataSource = this.getLoggedinUser();
      let name = this.dataSource.Email.split('@', 2);
      this.onLogin.emit(this.dataSource);
      this._snackBar.open('Signup completed successfully. Welcome ' + name[0] + '!', 'OK', { duration: this._successMsgDuration });
      
      /*
      we want to stay in the root url to prevent the error of 404 file or directory not found when users refresh page from a browser. 
      this._router.navigate(['/home']);
      */
      this._router.navigate(['']);
    }      
    else {
      let alertStr = coreResponseMsg.Message + ' Signning up got failed. Please try again.';
      this.openAlertDialog('Sign up', alertStr, '400px', '215px');
      this.handleCoreResponseMessageError(coreResponseMsg);
    }
  }

  cancel(): void {
    //this._functionsService.goBack();
    
    /*
    we want to stay in the root url to prevent the error of 404 file or directory not found when users refresh page from a browser. 
    this._router.navigate(['/home']);
    */
    this._router.navigate(['']);
  }

  forgotPasswordClick() {
    const dialogRef = this.dialog.open(ForgotPasswordComponent, {
      width: '540px',
      height: '300px',
      data: {
        parentComponent: 'LoginComponent'
      }
    });

    dialogRef.afterClosed().subscribe(userEmail => {
      if (userEmail !== '') {
        this.email = userEmail;
      }
    });
  }

  private isSubmittable(): boolean {    
    if (this.email && this.password)
      return this.email.length > 0 && this.password.length > 0;  
    else
      return false;
  }

  private toggleMenu(opened: boolean): void {
    if (opened)
      this.drawer.open();
    else
      this.drawer.close();
  }

  private setComponentHeight() {
    let screenHeight: number = window.innerHeight;
    let screenWidth: number = window.innerWidth;
    this.componentHeight = screenHeight - this._functionsService.getSubtractionForAppHeight(this.isUserAdmin()) + 'px';    
  }

  private cleanupInputs() {
    this.email = '';
    this.password = '';
  }
}
